import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import warehouseService from '../../services/warehouseService';
import toast from 'react-hot-toast';

export const fetchListings = createAsyncThunk(
  'warehouse/fetchListings',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await warehouseService.getListings(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch listings');
    }
  }
);

export const fetchListing = createAsyncThunk(
  'warehouse/fetchListing',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await warehouseService.getListing(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch listing');
    }
  }
);

export const createListing = createAsyncThunk(
  'warehouse/createListing',
  async (listingData, { rejectWithValue }) => {
    try {
      const { data } = await warehouseService.createListing(listingData);
      toast.success('Listing created successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create listing');
    }
  }
);

export const updateListing = createAsyncThunk(
  'warehouse/updateListing',
  async ({ id, data: listingData }, { rejectWithValue }) => {
    try {
      const { data } = await warehouseService.updateListing(id, listingData);
      toast.success('Listing updated successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update listing');
    }
  }
);

export const searchMarketplace = createAsyncThunk(
  'warehouse/searchMarketplace',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await warehouseService.searchListings(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Search failed');
    }
  }
);

const initialState = {
  listings: [],
  currentListing: null,
  totalCount: 0,
  loading: false,
  detailLoading: false,
  error: null,
  // Marketplace search
  searchResults: [],
  searchTotalCount: 0,
  searchLoading: false,
  // Distinguish "search returned nothing" from "request failed". Without
  // this flag the UI rendered an identical "0 warehouses found" state for
  // both, hiding backend cold-starts as phantom data bugs.
  searchError: null,
  searchFilters: {
    city: '',
    pincode: '',
    commodity_type: '',
    min_area: '',
    max_area: '',
    min_price: '',
    max_price: '',
    warehouse_type: '',
  },
};

const warehouseSlice = createSlice({
  name: 'warehouse',
  initialState,
  reducers: {
    clearCurrentListing: (state) => {
      state.currentListing = null;
    },
    setSearchFilters: (state, action) => {
      state.searchFilters = { ...state.searchFilters, ...action.payload };
    },
    resetSearchFilters: (state) => {
      state.searchFilters = initialState.searchFilters;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchListings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.loading = false;
        const d = action.payload;
        state.listings = Array.isArray(d?.results) ? d.results : Array.isArray(d?.items) ? d.items : Array.isArray(d?.listings) ? d.listings : Array.isArray(d) ? d : [];
        state.totalCount = d?.count || d?.total || state.listings.length || 0;
      })
      .addCase(fetchListings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchListing.pending, (state) => {
        state.detailLoading = true;
      })
      .addCase(fetchListing.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.currentListing = action.payload;
      })
      .addCase(fetchListing.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })
      .addCase(createListing.fulfilled, (state, action) => {
        state.listings.unshift(action.payload);
      })
      .addCase(updateListing.fulfilled, (state, action) => {
        state.currentListing = action.payload;
        const idx = state.listings.findIndex((l) => l.id === action.payload.id);
        if (idx !== -1) state.listings[idx] = action.payload;
      })
      .addCase(searchMarketplace.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchMarketplace.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchError = null;
        state.searchResults = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.searchTotalCount = action.payload?.total ?? action.payload?.count ?? 0;
      })
      .addCase(searchMarketplace.rejected, (state, action) => {
        state.searchLoading = false;
        // ``BACKEND_COLD_START`` is the sentinel set by the axios
        // interceptor when Azure returns the stop-page HTML. Surface
        // it as a distinct state so the UI can render a "wake-up in
        // progress" panel instead of silently showing "0 results".
        state.searchError = action.payload || 'SEARCH_FAILED';
      });
  },
});

export const { clearCurrentListing, setSearchFilters, resetSearchFilters, clearError } =
  warehouseSlice.actions;
export default warehouseSlice.reducer;
